import type { ClassSummary, StudentGuardianLink, StudentSummary } from '@school-erp/types';
import {
  GUARDIAN_RELATIONSHIPS,
  STUDENT_STATUS_TRANSITIONS,
  updateStudentSchema,
} from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';

export function StudentDetailPage() {
  const { studentId = '' } = useParams();
  const student = useQuery({
    queryKey: ['students', studentId],
    queryFn: () => api<StudentSummary>(`/students/${studentId}`),
  });

  if (student.isPending) return <p role="status">{t('common.loading')}</p>;
  if (student.isError)
    return (
      <p role="alert" className="text-red-700">
        {t('common.loadError')}
      </p>
    );
  const s = student.data;

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <div>
        <Link to="/students" className="text-sm text-brand-700 underline">
          {t('students.back')}
        </Link>
        <h1 className="mt-1 text-2xl font-bold">
          {s.firstName} {s.lastName}
        </h1>
        <p className="font-mono text-xs text-slate-600">
          {s.admissionNumber} · {t(`students.status.${s.status}` as MessageKey)}
        </p>
      </div>
      <Profile student={s} />
      <StatusSection student={s} />
      <EnrollmentSection student={s} />
      <GuardiansSection student={s} />
    </div>
  );
}

function useRefresh(studentId: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['students', studentId] }),
      queryClient.invalidateQueries({ queryKey: ['students'] }),
    ]);
}

function Profile({ student }: { student: StudentSummary }) {
  const { can } = useAuth();
  const editable = can('students.update', student.schoolId);
  const refresh = useRefresh(student.id);
  const initial = {
    firstName: student.firstName,
    lastName: student.lastName,
    phone: student.phone ?? '',
    email: student.email ?? '',
    house: student.house ?? '',
    medicalNotes: student.medicalNotes ?? '',
  };
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => setForm(initial), [student]);

  const showMedical = 'medicalNotes' in student; // present only if the API allowed it
  const save = useMutation({
    mutationFn: (body: object) => api(`/students/${student.id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      setMessage({ kind: 'ok', text: t('common.saved') });
      void refresh();
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setMessage(null);
    const body: Record<string, unknown> = { ...form };
    if (!showMedical) delete body.medicalNotes;
    const parsed = updateStudentSchema.safeParse(body);
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    save.mutate(parsed.data);
  }

  const bind = (name: keyof typeof initial) => ({
    value: form[name],
    error: errors[name],
    disabled: !editable,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [name]: e.target.value }),
  });

  return (
    <section aria-labelledby="profile-title" className="space-y-3">
      <h2 id="profile-title" className="text-lg font-semibold">
        {t('students.profile')}
      </h2>
      <form
        onSubmit={onSubmit}
        noValidate
        className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2"
      >
        <TextField label={t('students.firstName')} {...bind('firstName')} />
        <TextField label={t('students.lastName')} {...bind('lastName')} />
        <TextField label={t('students.phone')} {...bind('phone')} />
        <TextField label={t('students.email')} {...bind('email')} />
        <TextField label={t('students.house')} {...bind('house')} />
        {showMedical && <TextField label={t('students.medical')} {...bind('medicalNotes')} />}
        <div className="space-y-2 sm:col-span-2">
          {message && <Notice kind={message.kind}>{message.text}</Notice>}
          {editable && (
            <button type="submit" disabled={save.isPending} className={primaryButton}>
              {t('common.save')}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}

function StatusSection({ student }: { student: StudentSummary }) {
  const { can } = useAuth();
  const refresh = useRefresh(student.id);
  const options = STUDENT_STATUS_TRANSITIONS[student.status];
  const [next, setNext] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () =>
      api(`/students/${student.id}/status`, {
        method: 'POST',
        body: { status: next, reason: reason || undefined },
      }),
    onSuccess: () => {
      setNext('');
      setReason('');
      setError(null);
      void refresh();
    },
    onError: (e) => setError(errorMessage(e)),
  });

  if (!can('students.update', student.schoolId) || options.length === 0) return null;
  return (
    <section aria-labelledby="status-title" className="space-y-3">
      <h2 id="status-title" className="text-lg font-semibold">
        {t('students.changeStatus')}
      </h2>
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-4">
        <div>
          <label htmlFor="next-status" className="block text-sm font-medium">
            {t('adm.status')}
          </label>
          <select
            id="next-status"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className={selectClass}
          >
            <option value="" />
            {options.map((o) => (
              <option key={o} value={o}>
                {t(`students.status.${o}` as MessageKey)}
              </option>
            ))}
          </select>
        </div>
        <TextField
          label={t('students.statusReason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <button
          type="button"
          disabled={!next || change.isPending}
          onClick={() => change.mutate()}
          className={primaryButton}
        >
          {t('common.save')}
        </button>
        {error && <Notice kind="error">{error}</Notice>}
      </div>
    </section>
  );
}

function EnrollmentSection({ student }: { student: StudentSummary }) {
  const { can } = useAuth();
  const refresh = useRefresh(student.id);
  const classes = useQuery({
    queryKey: ['classes', student.schoolId],
    queryFn: () => api<ClassSummary[]>(`/schools/${student.schoolId}/classes`),
  });
  const [classId, setClassId] = useState(student.enrollment?.classId ?? '');
  const [sectionId, setSectionId] = useState(student.enrollment?.sectionId ?? '');
  const [roll, setRoll] = useState(student.enrollment?.rollNumber ?? '');
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const sections = classes.data?.find((c) => c.id === classId)?.sections ?? [];

  const save = useMutation({
    mutationFn: () =>
      api(`/students/${student.id}/enrollment`, {
        method: 'PUT',
        body: { classId, ...(sectionId && { sectionId }), ...(roll && { rollNumber: roll }) },
      }),
    onSuccess: () => {
      setMessage({ kind: 'ok', text: t('common.saved') });
      void refresh();
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });
  const editable = can('students.update', student.schoolId);

  return (
    <section aria-labelledby="enrollment-title" className="space-y-3">
      <h2 id="enrollment-title" className="text-lg font-semibold">
        {t('students.enrollment')}
      </h2>
      <div className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3">
        <div>
          <label htmlFor="enr-class" className="block text-sm font-medium">
            {t('students.class')}
          </label>
          <select
            id="enr-class"
            disabled={!editable}
            value={classId}
            onChange={(e) => {
              setClassId(e.target.value);
              setSectionId('');
            }}
            className={selectClass}
          >
            <option value="" />
            {classes.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="enr-section" className="block text-sm font-medium">
            {t('students.section')}
          </label>
          <select
            id="enr-section"
            disabled={!editable || !classId}
            value={sectionId}
            onChange={(e) => setSectionId(e.target.value)}
            className={selectClass}
          >
            <option value="">{t('students.noSection')}</option>
            {sections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <TextField
          label={t('students.rollNumber')}
          disabled={!editable}
          value={roll}
          onChange={(e) => setRoll(e.target.value)}
        />
        <div className="space-y-2 sm:col-span-3">
          {message && <Notice kind={message.kind}>{message.text}</Notice>}
          {editable && (
            <button
              type="button"
              disabled={!classId || save.isPending}
              onClick={() => save.mutate()}
              className={primaryButton}
            >
              {t('students.saveEnrollment')}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function GuardiansSection({ student }: { student: StudentSummary }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const key = ['students', student.id, 'guardians'];
  const guardians = useQuery({
    queryKey: key,
    queryFn: () => api<StudentGuardianLink[]>(`/students/${student.id}/guardians`),
  });
  const editable = can('students.update', student.schoolId);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    relationship: 'mother',
  });
  const [message, setMessage] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  const link = useMutation({
    mutationFn: () =>
      api(`/students/${student.id}/guardians`, {
        method: 'POST',
        body: {
          guardian: {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            phone: form.phone,
          },
          relationship: form.relationship,
          isPrimary: (guardians.data?.length ?? 0) === 0,
          isEmergencyContact: true,
        },
      }),
    onSuccess: () => {
      setAdding(false);
      setForm({ firstName: '', lastName: '', email: '', phone: '', relationship: 'mother' });
      setMessage(null);
      void refresh();
    },
    onError: (e) => setMessage(errorMessage(e)),
  });
  const unlink = useMutation({
    mutationFn: (guardianId: string) =>
      api(`/students/${student.id}/guardians/${guardianId}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });

  return (
    <section aria-labelledby="guardians-title" className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 id="guardians-title" className="text-lg font-semibold">
          {t('students.guardians')}
        </h2>
        {editable && (
          <button
            type="button"
            aria-expanded={adding}
            className={secondaryButton}
            onClick={() => setAdding((o) => !o)}
          >
            {adding ? t('common.cancel') : t('students.addGuardian')}
          </button>
        )}
      </div>
      {message && <Notice kind="error">{message}</Notice>}
      {adding && (
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            link.mutate();
          }}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3"
        >
          <TextField
            label={t('students.firstName')}
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
          />
          <TextField
            label={t('students.lastName')}
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
          />
          <div>
            <label htmlFor="g-rel" className="block text-sm font-medium">
              {t('students.relationship')}
            </label>
            <select
              id="g-rel"
              value={form.relationship}
              onChange={(e) => setForm({ ...form, relationship: e.target.value })}
              className={selectClass}
            >
              {GUARDIAN_RELATIONSHIPS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <TextField
            label={t('students.email')}
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <TextField
            label={t('students.phone')}
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <div className="self-end">
            <button type="submit" disabled={link.isPending} className={primaryButton}>
              {t('common.add')}
            </button>
          </div>
        </form>
      )}
      {guardians.data && (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
          {guardians.data.length === 0 && (
            <li className="p-4 text-slate-600">{t('students.noGuardians')}</li>
          )}
          {guardians.data.map((g) => (
            <li key={g.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
              <span>
                <span className="font-medium">
                  {g.firstName} {g.lastName}
                </span>{' '}
                <span className="text-slate-600">({g.relationship})</span>{' '}
                {g.isPrimary && (
                  <span className="rounded bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-900">
                    {t('students.primary')}
                  </span>
                )}{' '}
                {g.isEmergencyContact && (
                  <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                    {t('students.emergency')}
                  </span>
                )}
                <span className="block text-xs text-slate-600">
                  {[g.phone, g.email].filter(Boolean).join(' · ')}
                </span>
              </span>
              {editable && (
                <button
                  type="button"
                  onClick={() => unlink.mutate(g.id)}
                  className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                >
                  {t('students.unlink')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
